// Lib
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import classnames from 'classnames';
import { useDebounce } from 'use-debounce';
import { Autocomplete } from '@mui/material';

// Components
import Input from '../Field/Input';
import Feather from '../Feather';

// Actions
import { ToClearSearchLocalValue } from '../../actions/support';

// Styles
import { useStyles } from './SearchStyles';

const DEBOUNCE_DELAY_RATIO = 0.8;

const SearchInput = ({
  classes,
  className,
  value,
  ref,
  withIcon,
  loading,
  onClear,
  onChangeValue,
  autoFocus,
  ...props
}) => (
  <Input
    {...props}
    id="search-field"
    ref={ref}
    autoFocus={autoFocus}
    onChange={onChangeValue}
    className={classnames(classes.root, className)}
    placeholder="Search"
    value={value}
    InputProps={{
      ...props.InputProps,
      startAdornment:
        withIcon &&
        (loading ? (
          <div className={classes.progress}>
            <CircularProgress />
          </div>
        ) : (
          <SearchIcon className={classes.searchIcon} />
        )),
      endAdornment: value && (
        <Feather onClick={onClear} type="clear" nativeColor />
      ),
    }}
  />
);

// eslint-disable-next-line react/display-name
const Search = forwardRef(
  (
    {
      loadingDelay = 500,
      className,
      withIcon = true,
      onChange = () => {},
      autoFocus = false,
      onClearSearch,
      withMenu = false,
      options,
    },
    ref,
  ) => {
    const { search } = useSelector((store) => store);
    const [value, setValue] = useState(search);
    const [loading, setLoading] = useState(false);
    const [debouncedValue] = useDebounce(
      value,
      loadingDelay * DEBOUNCE_DELAY_RATIO,
    );
    const { toClearFAQSearch } = useSelector((state) => state.support);
    const dispatch = useDispatch();

    const showMenu = useMemo(() => {
      return withMenu && options.length > 0;
    }, [withMenu, options]);

    const { classes } = useStyles({ showMenu });

    useEffect(() => {
      onChange(debouncedValue);
      const timeoutId = setTimeout(() => setLoading(false), loadingDelay);
      return () => clearTimeout(timeoutId);
    }, [loadingDelay, debouncedValue, onChange]);

    const onChangeValue = useCallback(
      ({ value }) => {
        setLoading(true);
        setValue(value);
      },
      [setValue],
    );

    useEffect(() => {
      if (!search) {
        setValue('');
      }
    }, [search]);

    const onClear = useCallback(() => {
      onClearSearch(setValue);
    }, [onClearSearch, setValue]);

    useEffect(() => {
      if (toClearFAQSearch) {
        onClear();
        dispatch(ToClearSearchLocalValue(false));
      }
    }, [toClearFAQSearch, onClear, dispatch]);

    // for autocomplete
    const modifiedOptions = useMemo(
      () => (showMenu ? [{ length: options?.length }, ...options] : []),
      [options, showMenu],
    );

    return (
      <>
        {withMenu ? (
          <Autocomplete
            filterOptions={(options) => options}
            disablePortal
            classes={{
              paper: classes.paper,
              listbox: classes.listbox,
            }}
            inputValue={value}
            getOptionLabel={(option) => {
              return option.last_name || '';
            }}
            clearIcon={false}
            renderOption={(props, option) => {
              if (option.length) {
                return (
                  <li
                    key={`${option.length} matches`}
                    className={classes.matchText}>
                    {`${option.length} Top Matches`}
                  </li>
                );
              }
              if (option.length === 0) {
                return (
                  <li key="No matches" className={classes.matchText}>
                    No Items Found
                  </li>
                );
              }
              let value = `${option.first_name} ${option.last_name}`;
              if (option.company) {
                value = `${value}, ${option.company}`;
              }
              return (
                <li
                  {...props}
                  key={option.id}
                  className={classes.option}
                  onClick={(e) => {
                    props.onClick(e);
                    setValue(value);
                  }}>
                  {value}
                </li>
              );
            }}
            id="autocomplete-input"
            options={modifiedOptions}
            renderInput={(params) => {
              return (
                <SearchInput
                  {...params}
                  value={value}
                  autoFocus={autoFocus}
                  onChangeValue={onChangeValue}
                  className={className}
                  classes={classes}
                  loading={loading}
                  onClear={onClear}
                  ref={ref}
                  withIcon={withIcon}
                />
              );
            }}
          />
        ) : (
          <SearchInput
            value={value}
            autoFocus={autoFocus}
            onChangeValue={onChangeValue}
            className={className}
            classes={classes}
            loading={loading}
            onClear={onClear}
            ref={ref}
            withIcon={withIcon}
          />
        )}
      </>
    );
  },
);

export default Search;
